import {createCircle, createTriangle, createZigZag} from "./confetti-shapes.ts";
import type {Shape} from "./types.ts";

export interface Props {
    // The width of the HTML canvas. No default value provided.
    width?: number;
    // The height of the HTML canvas. No default value provided.
    height?: number;
    // An array of shapes, used when generating particles
    // (each item provided has an equal chance of being selected as a
    // particle)
    shapes?: Array<Shape>;
    // The number of particles to generate, spread over the
    // `emitDuration` length.
    numParticles?: number;
    // The amount of downward acceleration to provide.
    // Range: 10 = very slow, 10,000 = very fast.
    gravity?: number;
    // The amount of Z-axis (2D) rotation to provide to each particle.
    // Each particle has a random number specified, between 0 and n.
    // Range: 0 = no spin, 10 = reasonable spin, 100 = pukeville
    spin?: number;
    // The amount of X-axis (3D) rotation to provide to each particle.
    // Each particle has a random number specified, between 0 and n.
    // Range: 0 = no twist, 10 = reasonable twist, 100 = hummingbird
    twist?: number;
    // Each particle will have a random speed assigned, contained by
    // `minSpeed` and `maxSpeed`.
    // This is the base speed, which is affected by `gravity`.
    minSpeed?: number;
    maxSpeed?: number;
    // Each particle will have a random size applied, contained by
    // `minScale` and `maxScale`. If you'd like all particles to retain
    // their original size, simply provide `1` for both values.
    minScale?: number;
    maxScale?: number;
    // Amount of time to spread the release of particles, in milliseconds.
    emitDuration?: number;
    // Whether or not to center the particles around the mouse location,
    // or to distribute them evenly horizontally.
    spreadHorizontally?: boolean;
    // Callback triggered when animation ends.
    // NOTE: Only fires when all particles are off-canvas. Retriggering
    // the confetti before it's completed will delay the handler.
    onComplete?: () => void;
}

type DefaultProps = Required<Omit<Props, "spreadHorizontally" | "onComplete">>;

export const defaultProps: DefaultProps = {
    shapes: [
        createZigZag({fill: "#ca337c"}) as Shape, // Pink
        createZigZag({fill: "#01d1c1"}) as Shape, // Turquoise
        createZigZag({fill: "#f4d345"}) as Shape, // Yellow
        createCircle({fill: "#63d9ea"}) as Shape, // Blue
        createCircle({fill: "#ed5fa6"}) as Shape, // Pink
        createCircle({fill: "#aa87ff"}) as Shape, // Purple
        createCircle({fill: "#26edd5"}) as Shape, // Turquoise
        createTriangle({fill: "#ed5fa6"}) as Shape, // Pink
        createTriangle({fill: "#aa87ff"}) as Shape, // Purple
        createTriangle({fill: "#26edd5"}) as Shape, // Turquoise
    ],
    numParticles: 80,
    gravity: 1600,
    spin: 20,
    twist: 0,
    minSpeed: 225,
    maxSpeed: 675,
    minScale: 0.4,
    maxScale: 1.0,
    // By default emit all particles at once.
    emitDuration: 0,
    height: 0,
    width: 0,
};
